import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { useRefState } from '@bay-email/editor';
import kvstore from '~/services/kvstore.js';

// SketchPicker default preset colors (matches react-color SketchPicker exactly)
const defaultPresetColor: string[] = [
	'#D0021B', // Red
	'#F5A623', // Orange
	'#F8E71C', // Yellow
	'#8B572A', // Brown
	'#7ED321', // Green
	'#417505', // Dark Green
	'#BD10E0', // Purple
	'#9013FE', // Violet
	'#4A90E2', // Blue
	'#50E3C2', // Cyan
	'#B8E986', // Light Green
	'#000000', // Black
	'#4A4A4A', // Dark Gray
	'#9B9B9B', // Gray
	'#FFFFFF', // White
];

const KVSTORE_COMPONENT_KEY = 'emailEditorPresetColors';
const MAX_RECORD_SIZE = 20;

export const PresetColorsContext = React.createContext<{
	colors: string[];
	addCurrentColor: (color: string) => void;
}>({
	colors: [],
	addCurrentColor: () => {},
});

export const PresetColorsProvider: React.FC<{
	children: React.ReactNode | React.ReactElement;
}> = props => {
	const [currentColors, setCurrentColors] = useState<string[]>(defaultPresetColor);
	const currentColorsRef = useRefState(currentColors);
	const userIdRef = useRef<string | number | null>(null);
	const colorDivRef = useRef(document.createElement('div'));

	const userId: string | number | null = useSelector(
		(state: any) =>
			state?.actingAs?.customer?.idNumber ?? state?.user?.customer?.idNumber ?? null,
	);

	// Load colors from kvstore when userId is available
	useEffect(() => {
		const loadColors = async () => {
			try {
				if (!userId) return;

				userIdRef.current = userId;

				const result = await kvstore.read({
					component: KVSTORE_COMPONENT_KEY,
					userId,
				});

				if (
					result?.data?.val &&
					Array.isArray(result.data.val) &&
					result.data.val.length > 0
				) {
					setCurrentColors(result.data.val);
				}
			} catch (error) {
				console.error(
					'[PresetColorsProvider] Failed to load colors from kvstore:',
					error,
				);
			}
		};

		loadColors();
	}, [userId]);

	const saveToKvstore = useCallback(
		debounce(async (colors: string[]) => {
			try {
				if (!userIdRef.current) return;

				await kvstore.update({
					component: KVSTORE_COMPONENT_KEY,
					userId: userIdRef.current,
					data: colors,
				});
			} catch (error) {
				console.error('[PresetColorsProvider] Failed to save colors to kvstore:', error);
			}
		}, 1000),
		[],
	);

	const addCurrentColor = useCallback(
		debounce((newColor: string) => {
			colorDivRef.current.style.color = '';
			colorDivRef.current.style.color = newColor;
			if (colorDivRef.current.style.color) {
				if (currentColorsRef.current!.includes(newColor)) return;
				const newColors = [...new Set([...currentColorsRef.current!, newColor])]
					.filter(Boolean)
					.slice(-MAX_RECORD_SIZE);

				setCurrentColors(newColors);
				saveToKvstore(newColors);
			}
		}, 500),
		[currentColorsRef, saveToKvstore],
	);

	const value = useMemo(() => {
		return {
			colors: currentColors!,
			addCurrentColor,
		};
	}, [addCurrentColor, currentColors]);

	return useMemo(() => {
		return (
			<PresetColorsContext.Provider value={value}>
				{props.children}
			</PresetColorsContext.Provider>
		);
	}, [props.children, value]);
};
